/**
 * @prettier
 **/
// @ts-check

export const columnSettings = {
  name: {
    name: 'name',
    class: 'tablesort-sortable',
    stSort: 'name',
    displayName: 'PUBLISHERS_NAME',
    sort: 'asc',
    defaultSort: 'asc',
    rowAlign: 'left'
  },
  id: {
    name: 'id',
    displayName: 'PUBLISHERS_ID',
    class: 'tablesort-sortable',
    sort: 'asc',
    rowAlign: 'left'
  },
  createNumbers: {
    name: 'createNumbers',
    class: 'tablesort-sortable',
    stSort: 'createNumbers',
    displayName: 'PUBLISHERS_PERMISSIONS_NUMBER',
    rowAlign: 'left'
  },
  currentLive: {
    name: 'currentLive',
    class: 'tablesort-sortable text-right',
    stSort: 'currentLive',
    displayName: 'PUBLISHERS_CURRENT_LIVE',
    rowAlign: 'right'
  },
  currentHour: {
    name: 'currentHour',
    class: 'tablesort-sortable text-right',
    stSort: 'currentHour',
    displayName: 'PUBLISHERS_CURRENT_HOUR',
    rowAlign: 'right'
  },
  currentDay: {
    name: 'currentDay',
    class: 'tablesort-sortable text-right',
    stSort: 'currentDay',
    displayName: 'PUBLISHERS_CURRENT_DAY',
    rowAlign: 'right'
  },
  currentMonth: {
    name: 'currentMonth',
    class: 'tablesort-sortable text-right',
    stSort: 'currentMonth',
    displayName: 'PUBLISHERS_CURRENT_MONTH',
    rowAlign: 'right'
  },
  total: {
    name: 'total',
    class: 'tablesort-sortable text-right',
    stSort: 'total',
    displayName: 'PUBLISHERS_TOTAL',
    rowAlign: 'right'
  },
  enabled: {
    name: 'enabled',
    class: 'tablesort-sortable',
    stSort: 'enabled',
    displayName: 'PUBLISHERS_STATUS',
    rowAlign: 'center'
  }
};

export const defaultColumns = [
  'name',
  'id',
  'createNumbers',
  'currentLive',
  'currentHour',
  'currentDay',
  'currentMonth',
  'total',
  'enabled'
];

export default class PublisherService {
  /**
   * @ngInject
   * @param {import('angular').IHttpService} $http
   * @param {*} $q
   * @param {*} ngAuthSettings
   * @param {*} promiseCacheService
   * @param {import('lodash').LoDashStatic} _
   */

  constructor(
    _,
    $http,
    $q,
    ngAuthSettings,
    accountsService,
    promiseCacheService
  ) {
    this._ = _;
    this.$http = $http;
    this.$q = $q;
    this.ngAuthSettings = ngAuthSettings;
    this.serviceBaseUri = ngAuthSettings.apiServiceBaseUri;
    this.promiseCacheService = promiseCacheService;
    this.accountsService = accountsService;
    this.columnSettings = _.cloneDeep(columnSettings);
    this.defaultColumns = _.cloneDeep(defaultColumns);
  }

  // protected methods
  #getCacheKey(accountId, getStats) {
    return (
      'affiliatesGetPromise' + accountId + (getStats ? 'includestats' : '')
    );
  }

  #canView() {
    return (
      !this.accountsService.isAffiliate() && !this.accountsService.isBuyer()
    );
  }

  #serviceBaseUrl(accountId, affiliateId) {
    return this.serviceBaseUri + accountId + '/affiliates/' + affiliateId;
  }

  async _makeGetRequest(url) {
    try {
      if (this.#canView()) {
        const response = await this.$http({
          url: url,
          method: 'GET'
        });

        return response;
      } else {
        let deferred = this.$q.defer();

        deferred.resolve({
          data: {
            affiliates: []
          }
        });

        return deferred.promise;
      }
    } catch {
      return { data: { affiliates: [] } };
    }
  }

  #sanitizeAffiliate(affiliate) {
    let sanitized = {
      id: affiliate.id,
      accountId: affiliate.accountId
    };

    if (affiliate.name) {
      sanitized.name = affiliate.name;
    } else {
      sanitized.name = '';
    }

    if (affiliate.subId) {
      sanitized.subId = affiliate.subId;
    } else {
      sanitized.subId = '';
    }
    if (typeof affiliate.enabled !== 'undefined') {
      sanitized.enabled = affiliate.enabled;
    }
    sanitized.createNumbers = !!affiliate.createNumbers;

    sanitized.blockCallsIfCapped = affiliate.blockCallsIfCapped;
    sanitized.accessToRecordings = affiliate.accessToRecordings;

    if (affiliate.isSelf) {
      sanitized.isSelf = affiliate.isSelf;
    }
    if (affiliate.loginId) {
      sanitized.loginId = affiliate.loginId;
    }
    return sanitized;
  }

  // public methods
  async getAffiliates(accountId, getStats) {
    const url =
      this.serviceBaseUri +
      accountId +
      '/affiliates' +
      (getStats ? '?includestats=true' : '');

    return await this._makeGetRequest(url);
  }

  async getCachedAffiliates(accountId, getStats) {
    const url =
      this.serviceBaseUri +
      accountId +
      '/affiliates' +
      (getStats ? '?includestats=true' : '');

    try {
      return await this.promiseCacheService.getCallWithCachedPromise(
        this.#getCacheKey(accountId, getStats),
        url,
        30
      );
    } catch {
      return {
        data: {
          affiliates: []
        }
      };
    }
  }

  getAffiliatesStats(accountId) {
    const url = this.serviceBaseUri + accountId + '/affiliates/stats';
    return this._makeGetRequest(url);
  }

  getAffiliate(accountId, affiliateId) {
    const url = this.#serviceBaseUrl(accountId, affiliateId);
    return this._makeGetRequest(url);
  }

  createAffiliate(accountId, affiliate) {
    const url = this.serviceBaseUri + accountId + '/affiliates';
    return this.$http.post(url, affiliate);
  }

  updateAffiliate(accountId, affiliate) {
    const url = this.#serviceBaseUrl(accountId, affiliate.id);
    const data = this.#sanitizeAffiliate(affiliate);
    return this.$http.patch(url, data);
  }

  getAffiliateReferences(accountId, affiliateId) {
    const url =
      this.#serviceBaseUrl(accountId, affiliateId) + '/InboundReferences';
    return this.$http.get(url);
  }

  deleteAffiliate(accountId, affiliateId, force, keepNumbers) {
    const url =
      this.#serviceBaseUrl(accountId, affiliateId) +
      '?force=' +
      force +
      '&keepNumbers=' +
      keepNumbers;
    return this.$http.delete(url);
  }

  // the rest of the methods are not really needed for the list of publishers
  // but keeping them here to decide later on

  // being used in PayoutSettings.vue and in campaign controller components
  initializePayoutCaps(payoutSettings) {
    const payoutCaps = {};
    try {
      if (payoutSettings.allTimeCap > -1) {
        payoutCaps.allTimeCap = 1;
      } else {
        delete payoutSettings.allTimeCap;
        payoutCaps.allTimeCap = -1;
      }
      if (payoutSettings.monthlyCap > -1) {
        payoutCaps.monthlyCap = 1;
      } else {
        delete payoutSettings.monthlyCap;
        payoutCaps.monthlyCap = -1;
      }
      if (payoutSettings.dailyCap > -1) {
        payoutCaps.dailyCap = 1;
      } else {
        delete payoutSettings.dailyCap;
        payoutCaps.dailyCap = -1;
      }
      if (payoutSettings.hourlyCap > -1) {
        payoutCaps.hourlyCap = 1;
      } else {
        delete payoutSettings.hourlyCap;
        payoutCaps.hourlyCap = -1;
      }
      if (payoutSettings.concurrencyCap > -1) {
        payoutCaps.concurrencyCap = 1;
      } else {
        delete payoutSettings.concurrencyCap;
        payoutCaps.concurrencyCap = -1;
      }

      if (payoutSettings.allTimeSumCap > -1) {
        payoutCaps.allTimeSumCap = 1;
      } else {
        delete payoutSettings.allTimeSumCap;
        payoutCaps.allTimeSumCap = -1;
      }
      if (payoutSettings.monthlySumCap > -1) {
        payoutCaps.monthlySumCap = 1;
      } else {
        delete payoutSettings.monthlySumCap;
        payoutCaps.monthlySumCap = -1;
      }
      if (payoutSettings.dailySumCap > -1) {
        payoutCaps.dailySumCap = 1;
      } else {
        delete payoutSettings.dailySumCap;
        payoutCaps.dailySumCap = -1;
      }
      if (payoutSettings.hourlySumCap > -1) {
        payoutCaps.hourlySumCap = 1;
      } else {
        delete payoutSettings.hourlySumCap;
        payoutCaps.hourlySumCap = -1;
      }
    } catch (e) {
      // empty
    }
    return payoutCaps;
  }

  // being used in BuyerForm.vue and in payout-settings.component.js components
  resetCallCaps(payoutCaps, payoutSettings) {
    payoutCaps = payoutCaps || {};
    payoutSettings = payoutSettings || {};
    if (payoutCaps.allTimeCap === -1) {
      payoutSettings.allTimeCap = -1;
    }
    if (payoutCaps.monthlyCap === -1) {
      payoutSettings.monthlyCap = -1;
    }
    if (payoutCaps.dailyCap === -1) {
      payoutSettings.dailyCap = -1;
    }
    if (payoutCaps.hourlyCap === -1) {
      payoutSettings.hourlyCap = -1;
    }
    if (payoutCaps.concurrencyCap === -1) {
      payoutSettings.concurrencyCap = -1;
    }

    if (payoutCaps.allTimeSumCap === -1) {
      payoutSettings.allTimeSumCap = -1;
    }
    if (payoutCaps.monthlySumCap === -1) {
      payoutSettings.monthlySumCap = -1;
    }
    if (payoutCaps.dailySumCap === -1) {
      payoutSettings.dailySumCap = -1;
    }
    if (payoutCaps.hourlySumCap === -1) {
      payoutSettings.hourlySumCap = -1;
    }
    return payoutCaps;
  }

  // being used in campaign-wizard.component.js
  isSelfPub(publisher) {
    if (!publisher) return false;
    if (publisher.isSelf) return true;

    const name = publisher.name.toLowerCase();
    return name === 'self' || name === 'you';
  }

  // being used in campaign-wizard.component.js
  async getNonSelfPublishers(accountId) {
    const response = await this.getAffiliates(accountId);
    return this._.filter(response.data.affiliates, function(o) {
      return !this.isSelfPub(o);
    });
  }

  // being used in campaign-wizard.component.js
  async getSelfPublisher(accountId) {
    const response = await this.getAffiliates(accountId);
    return this._.find(response.data.affiliates, function(o) {
      return this.isSelfPub(o);
    });
  }

  // being used in payout-settings.component.js
  removePayoutCaps(payoutSettings, payoutCaps) {
    delete payoutSettings.allTimeCap;
    delete payoutSettings.monthlyCap;
    delete payoutSettings.dailyCap;
    delete payoutSettings.hourlyCap;
    delete payoutSettings.concurrencyCap;

    delete payoutSettings.allTimeSumCap;
    delete payoutSettings.monthlySumCap;
    delete payoutSettings.dailySumCap;
    delete payoutSettings.hourlySumCap;

    this._.forOwn(payoutCaps, function(value, key) {
      payoutCaps[key] = -1;
    });
  }
}
